import { makeObservable, observable, flow, computed } from "mobx";
import { PenSquareIcon, Trash, Users } from "lucide-react";

import { api } from "@/lib/services";
import { ApiRoutes } from "@/lib/routes";

import UpdateAuditRegistryStore from "@/areas/settings/stores/UpdateAuditRegistryStore";
import DeleteAuditRegistryStore from "@/areas/settings/stores/DeleteAuditRegistryStore";
import AssignAuditRegistryStore from "@/areas/settings/stores/AssignAuditRegistryStore";

import SubscriberSummaryStore from "@/areas/subscriptions/stores/SubscriberSummaryStore";
import BrowseSubscribersStore from "@/areas/subscriptions/stores/BrowseSubscribersStore";
import AddSubscriberStore from "@/areas/subscriptions/stores/AddSubscriberStore";
import UpdateSubscriberStore from "@/areas/subscriptions/stores/UpdateSubscriberStore";
import RemoveSubscriberStore from "@/areas/subscriptions/stores/RemoveSubscriberStore";
import AuditRegistryPermissionStore from "@/areas/settings/stores/AuditRegistryPermissionStore";

export default class SelectedAuditRegistryStore {
  loading = false;
  registry = null;
  registryId = null;

  constructor(parentStore, registryId, registry) {
    makeObservable(this, {
      loading: observable,
      registry: observable,
      registryId: observable,
      load: flow,
      processing: computed,
      actions: computed,
      canView: computed,
    });

    this.parentStore = parentStore;
    this.registryId = registryId;
    this.registry = registry;

    this.assignAuditRegistryStore = new AssignAuditRegistryStore(this);
    this.updateAuditRegistryStore = new UpdateAuditRegistryStore(this);
    this.deleteAuditRegistryStore = new DeleteAuditRegistryStore(this);

    const subscriberApiPrefix = ApiRoutes.forAuditRegistry(registryId);
    this.subscriberSummaryStore = new SubscriberSummaryStore(this, subscriberApiPrefix);
    this.browseSubscribersStore = new BrowseSubscribersStore(this, subscriberApiPrefix);
    this.addSubscriberStore = new AddSubscriberStore(this, subscriberApiPrefix);
    this.updateSubscriberStore = new UpdateSubscriberStore(this, subscriberApiPrefix);
    this.removeSubscriberStore = new RemoveSubscriberStore(this, subscriberApiPrefix);
    this.auditRegistryPermissionStore = new AuditRegistryPermissionStore(this);
  }

  get processing() {
    return this.loading;
  }

  get actions() {
    const list = [];

    if (!this.registry) {
      return list;
    }

    list.push({
      id: "settings:editRegistry",
      label: "Edit Registry",
      icon: PenSquareIcon,
      onClick: () => {
        this.updateAuditRegistryStore.show();
        this.load({});
      },
      processing: true,
      disabled: !this.updateAuditRegistryStore.canEdit,
    });
    list.push({
      id: "settings:deleteRegistry",
      label: "Delete Registry",
      icon: Trash,
      onClick: () => {
        this.deleteAuditRegistryStore.show();
        this.load({});
      },
      disabled: null,
    });
    list.push({
      id: "settings:subscribers",
      label: "Subscribers",
      icon: Users,
      onClick: () => {
        this.browseSubscribersStore.show();
        this.load({});
        this.browseSubscribersStore.load({ search: "", page: 1 });
        this.parentStore.parentStore.complianceStore.browseAuditRegistryRolesStore.load({});
      },
      disabled: !this.canView,
    });

    return list;
  }

  get canView() {
    const isAdmin = this.parentStore.parentStore.authenticationStore.isAdmin;
    const hasSubscriptions = this.auditRegistryPermissionStore.hasSubscriptions;
    return isAdmin || hasSubscriptions;
  }

  *load({}) {
    if (!this.registryId) {
      return { success: false, message: "Registry id is required" };
    }
    try {
      this.loading = true;
      const response = yield api.get(ApiRoutes.forAuditRegistry(id));
      this.registry = response.data;

      return { success: true, data: response };
    } catch (e) {
      return { success: false, error: e };
    } finally {
      this.loading = false;
    }
  }
}
